/**
 * BoardTaskCard Component
 * Task card for the phase-based board view (no drag-drop)
 */

import { useState } from 'react';
import { Paper, Group, Text, Badge, Checkbox, Avatar, Stack, ActionIcon, Tooltip } from '@mantine/core';
import { Calendar, MessageSquare, List, Link as LinkIcon } from 'react-feather';
import type { TaskWithDetails, TaskStatus } from '../../../../types';
import { getPriorityConfig } from '../../../../config/taskConfig';
import { formatDateOnly } from '../../../../utils/formatters';
import { isDateOnlyPast } from '../../../../utils/dateHelpers';
import { copyTaskLink } from '../../../../utils/taskLinks';

interface BoardTaskCardProps {
  task: TaskWithDetails;
  onToggleComplete: (taskId: string, currentStatus: TaskStatus) => void;
  onClick: (task: TaskWithDetails) => void;
  /** MED-770: Cohort ID for generating copy-link URLs */
  cohortId?: string;
}

export function BoardTaskCard({
  task,
  onToggleComplete,
  onClick,
  cohortId,
}: BoardTaskCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const isComplete = task.status === 'complete';
  const priorityConfig = getPriorityConfig(task.priority);

  // Only show HIGH and MEDIUM priority badges
  const showPriorityBadge = task.priority === 'high' || task.priority === 'urgent';

  // Check if overdue
  const isOverdue =
    task.due_date &&
    isDateOnlyPast(task.due_date) &&
    task.status !== 'complete' &&
    task.status !== 'cancelled';

  return (
    <Paper
      withBorder
      radius="md"
      p="sm"
      mb="xs"
      style={{
        cursor: 'pointer',
        transition: 'box-shadow 0.15s ease',
        position: 'relative',
      }}
      onClick={() => onClick(task)}
      onMouseEnter={(e) => {
        setIsHovered(true);
        e.currentTarget.style.boxShadow = 'var(--mantine-shadow-sm)';
      }}
      onMouseLeave={(e) => {
        setIsHovered(false);
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {/* MED-770: Copy link icon — top-right, visible on hover */}
      {cohortId && isHovered && (
        <Tooltip label="Copy link to task" position="top" withArrow>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="xs"
            style={{ position: 'absolute', top: 6, right: 6 }}
            onClick={(e) => {
              e.stopPropagation();
              copyTaskLink(task.id, cohortId);
            }}
          >
            <LinkIcon size={12} />
          </ActionIcon>
        </Tooltip>
      )}

      <Stack gap="xs">
        {/* Sub-group Label */}
        {task.sub_group && (
          <Text
            size="xs"
            c="dimmed"
            tt="uppercase"
            fw={500}
            style={{ letterSpacing: '0.3px' }}
          >
            {task.sub_group}
          </Text>
        )}

        {/* Task Name with Checkbox */}
        <Group gap="xs" align="flex-start" wrap="nowrap">
          <Checkbox
            checked={isComplete}
            color="medleyGreen"
            size="sm"
            onChange={(e) => {
              e.stopPropagation();
              onToggleComplete(task.id, task.status);
            }}
            onClick={(e) => e.stopPropagation()}
            styles={{
              input: {
                cursor: 'pointer',
              },
            }}
          />
          <Text
            size="sm"
            fw={500}
            style={{
              textDecoration: isComplete ? 'line-through' : 'none',
              color: isComplete ? 'var(--mantine-color-dimmed)' : 'inherit',
              flex: 1,
            }}
          >
            {task.title}
          </Text>
        </Group>

        {/* Meta Row: Due Date, Priority, Counts */}
        <Group justify="space-between" gap="xs">
          <Group gap="xs">
            {/* Due Date */}
            {task.due_date && (
              <Group gap={4}>
                <Calendar
                  size={12}
                  color={isOverdue ? 'var(--mantine-color-dangerRed-6)' : 'var(--mantine-color-dimmed)'}
                />
                <Text
                  size="xs"
                  c={isOverdue ? 'dangerRed' : 'dimmed'}
                  fw={isOverdue ? 600 : 400}
                >
                  {formatDateOnly(task.due_date)}
                </Text>
              </Group>
            )}

            {/* Priority Badge */}
            {showPriorityBadge && (
              <Badge size="xs" color={priorityConfig.color} variant="filled">
                {priorityConfig.label.toUpperCase()}
              </Badge>
            )}

            {/* Subtasks Overdue Badge - only on parent tasks with overdue subtasks */}
            {!task.parent_task_id && task.has_overdue_subtasks && (
              <Badge size="xs" color="dangerRed" variant="light">
                SUBTASKS OVERDUE
              </Badge>
            )}
          </Group>

          <Group gap="xs">
            {/* Subtask Count */}
            {task.subtask_count > 0 && (
              <Group gap={4}>
                <List size={12} color="var(--mantine-color-dimmed)" />
                <Text size="xs" c="dimmed">
                  {task.subtasks_completed}/{task.subtask_count}
                </Text>
              </Group>
            )}

            {/* Comment Count */}
            {task.comment_count > 0 && (
              <Badge
                size="sm"
                color="blue"
                variant="light"
                leftSection={<MessageSquare size={10} />}
              >
                {task.comment_count}
              </Badge>
            )}

            {/* Assignee Avatar */}
            {task.assignee_name && (
              <Avatar size="xs" radius="xl" color="medleyGreen">
                {task.assignee_name[0]?.toUpperCase()}
              </Avatar>
            )}
          </Group>
        </Group>
      </Stack>
    </Paper>
  );
}

export default BoardTaskCard;
